import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { RiStackLine, RiZoomOutLine, RiZoomInLine, RiListCheck3 } from 'react-icons/ri';
import { agentsLeftOutNote } from '../../utils/chatEntries';
import { toMdString } from '../../utils/formatContent';
import { sanitizeMarkdownViz } from '../../utils/markdownComponents';
import { shouldShowExpansiveSpinner } from '../../utils/expansiveStatus';
import InlineProgressCard from './ProgressDisplay';

// Stable plugin arrays so ReactMarkdown's internal memoization isn't
// defeated by a fresh array identity on every render.
const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeSanitize];

// Phrases that indicate the model is citing its own training cutoff rather
// than real-world data.  Matched against the preliminary answer text so we
// can nudge the user toward the Expansive process (which does live research).
const STALE_DATA_RE = /\b(training data|knowledge cutoff|as of my (knowledge|training|last update)|real-time (data|information|access)|I don't have (access to|real-time|current|live)|I do not have (access to|real-time|current|live)|based on my training|my (knowledge|training) (cutoff|ends|only goes|only extends|cut off)|I cannot access (live|current|real-time)|no (access|real-time access) to (current|live|recent))\b/i;

const NOTE_STYLE = {
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: 4,
  padding: '7px 11px',
  background: 'var(--bg-secondary, #f5f5f5)',
  border: '1px solid var(--border-color, #ddd)',
  borderRadius: 6,
  fontSize: '0.8rem',
  color: 'var(--text-secondary, #888)',
  lineHeight: 1.45,
};

const BTN_BASE = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '4px 10px', borderRadius: '6px', transition: 'all 0.2s ease',
  fontSize: '0.8rem', minWidth: '100px',
};

const BUBBLE_STYLE = {
  background: 'var(--primary-color, #636cff)',
  color: '#fff',
  borderRadius: '12px 12px 2px 12px',
  padding: '10px 14px',
  fontSize: '0.92rem',
  lineHeight: 1.5,
  wordBreak: 'break-word',
};

const CARD_STYLE = {
  background: 'var(--panel-background, var(--input-background))',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  overflow: 'hidden',
};

const CARD_HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--input-background)',
};

const CARD_LABEL_STYLE = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// Explicit allowlist — excludes SVG (image/svg+xml) which can carry inline scripts.
const SAFE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);

function FileAttachments({ files, fileObjectUrls, API_URL, conversationId, rawIndex }) {
  if (!files || files.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px', justifyContent: 'flex-end' }}>
      {files.map((file, fileIndex) => {
        const mediaType = file.media_type || file.type || '';
        const isImage = SAFE_IMAGE_TYPES.has(mediaType);
        const isPDF = mediaType === 'application/pdf';
        // Use a safe media_type in any data: URL to prevent type-confusion attacks.
        const safeMediaType = isImage ? mediaType : 'application/octet-stream';
        const dataUrl = fileObjectUrls
          ? fileObjectUrls.get(fileIndex)
          : file.data
            ? `data:${safeMediaType};base64,${file.data}`
            : `${API_URL}/chat_history/file/${encodeURIComponent(conversationId)}/${encodeURIComponent(rawIndex)}/${fileIndex}`;

        if (isImage) {
          return (
            <button
              key={fileIndex}
              type="button"
              aria-label={`Open image ${file.name || `Attachment ${fileIndex + 1}`} in new tab`}
              tabIndex={0}
              style={{ border: '2px solid rgba(255,255,255,0.3)', borderRadius: '8px', overflow: 'hidden', maxWidth: '200px', cursor: 'pointer', padding: 0, background: 'none', display: 'block' }}
              onClick={() => {
                // Never open blob: URLs in a new tab — SVG embedded in a blob can execute
                // scripts in the full page context. Use the sanitised data: URL instead.
                const safeUrl = dataUrl.startsWith('blob:')
                  ? `data:${safeMediaType};base64,${file.data || ''}`
                  : dataUrl;
                window.open(safeUrl, '_blank');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const safeUrl = dataUrl.startsWith('blob:')
                    ? `data:${safeMediaType};base64,${file.data || ''}`
                    : dataUrl;
                  window.open(safeUrl, '_blank');
                }
              }}
              title="Click to open image"
            >
              <img src={dataUrl} alt={file.name || `Attachment ${fileIndex + 1}`} style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
            </button>
          );
        } else if (isPDF) {
          return (
            <a key={fileIndex} href={dataUrl} download={file.name || `document-${fileIndex + 1}.pdf`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: '6px', textDecoration: 'none', color: '#fff', fontSize: '0.82rem' }}>
              📄 {file.name || `PDF ${fileIndex + 1}`}
            </a>
          );
        } else {
          return (
            <a key={fileIndex} href={dataUrl} download={file.name || `file-${fileIndex + 1}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: '6px', textDecoration: 'none', color: '#fff', fontSize: '0.82rem' }}>
              📎 {file.name || `File ${fileIndex + 1}`}
            </a>
          );
        }
      })}
    </div>
  );
}

// Why a run failed, in the same style wherever it shows.
const RunError = ({ message, markdownComponents }) => (
  <div className="error-text" style={{ padding: '0.75rem 0', whiteSpace: 'pre-wrap' }}>
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={markdownComponents}>
      {toMdString(message)}
    </ReactMarkdown>
  </div>
);

// Memoized single history entry.  Extracted so a per-token setSessions during
// streaming re-renders ONLY the actively-streaming entry (which gets a new
// object identity); committed entries keep reference equality and skip the
// expensive full-markdown re-parse (react-markdown v10 has no cross-render
// memo).  The parent passes derived primitives (activeView, isLatestEntry) so
// a tab click on one entry invalidates only that entry, and stabilised
// handlers (useCallback) so React.memo can actually bail.
const HistoryEntry = React.memo(function HistoryEntry({
  entry,
  entryKey,
  error,
  activeView,
  isLatestEntry,
  loading,
  isActiveConvServerRunning,
  streamingPhase,
  generatingExpansiveIndex,
  generatingEvaluationIndex,
  isExpansiveInProgress,
  conversationId,
  API_URL,
  markdownComponents,
  setActiveAnswerView,
  handleExpansiveClick,
  handleEvalClick,
  handleBtnTouch,
}) {
  const isRunning = loading || isActiveConvServerRunning;
  const hasPreliminary = Boolean(entry.preliminary_answer && entry.preliminary_answer !== entry.assistant);
  const canOpenEvaluationView = Boolean(entry.evaluation_report);
  const displayedAnswer =
    activeView === 'preliminary'
      ? entry.preliminary_answer || entry.assistant
      : activeView === 'evaluation'
      ? entry.evaluation_report || entry.assistant
      : entry.assistant;
  // Agents only write the text they could be left out of: the long answer
  // and the evaluation, not the concise answer.
  const leftOutNote = agentsLeftOutNote(
    activeView === 'evaluation'
      ? entry.evaluation_agents_left_out
      : activeView === 'preliminary' && hasPreliminary
      ? null
      : entry.agents_left_out,
  );
  // Show the tab switcher as soon as preliminary_answer exists so
  // users can click "Expansive" mid-stream to read it as it arrives.
  // The previous guard (!isGeneratingThisEntry) hid the tabs during
  // generation, making the expansive content unreachable until done.
  const showViewSwitcher = hasPreliminary;
  // crossSessionEvaluationRunning is intentionally NOT used
  // for the spinner.  When the server is running in another
  // session we cannot reliably distinguish "finishing the
  // expansive status update" from "running evaluation".
  // Using hasPreliminary && isActiveConvServerRunning caused
  // the Evaluation button to spin during the brief window
  // after _save_chain_result_to_history writes to DB but
  // before status="completed" is polled by the sidebar.
  // The 3-second poll reveals the evaluation result when
  // it lands, so no spinner is needed here.
  const isEvaluationButtonDisabled = isRunning && !canOpenEvaluationView;
  // Single source of truth for the Expansive "Generating…" state,
  // shared by the view-switcher tab (Branch A) and the generate button
  // (Branch B) so the live-stream and post-refresh views agree. See
  // shouldShowExpansiveSpinner for why the live case ignores
  // hasPreliminary.
  const showExpansiveSpinner = shouldShowExpansiveSpinner({
    loading,
    isLatestEntry,
    isActiveConvServerRunning,
    hasPreliminary,
    isExplicitExpansiveForEntry: generatingExpansiveIndex === entry.index,
    streamingPhase,
    isExpansiveInProgress,
  });
  const showEvaluationSpinner = loading && generatingEvaluationIndex === entry.index;
  const expansiveGenerateTooltip = showExpansiveSpinner ? 'Generating...' : isRunning ? 'Another query is processing' : 'Click to generate expansive answer';
  const evaluationTooltip = canOpenEvaluationView ? 'View Evaluation' : showEvaluationSpinner ? 'Generating...' : isRunning ? 'Another query is processing' : 'Click to generate evaluation answer';
  // Show a nudge when the preliminary answer explicitly references
  // the model's training cutoff and no expansive result exists yet.
  const showStaleDataNudge =
    !hasPreliminary &&
    !isRunning &&
    !showExpansiveSpinner &&
    STALE_DATA_RE.test(entry.preliminary_answer || entry.assistant || '');

  return (
    <div data-entry-index={entryKey} style={{ marginBottom: '1.5rem' }}>

      {/* User question — right-aligned blue bubble */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <div style={{ maxWidth: '80%' }}>
          <FileAttachments
            files={entry.files}
            API_URL={API_URL}
            conversationId={conversationId}
            rawIndex={entry.raw_index}
          />
          <div style={BUBBLE_STYLE}>{entry.user}</div>
        </div>
      </div>

      {/* Stack answer — left-aligned dark card */}
      <div style={CARD_STYLE}>
        <div style={CARD_HEADER_STYLE}>
          <RiStackLine size={15} />
          <span style={CARD_LABEL_STYLE}>Stack</span>
        </div>
        <div style={{ padding: '12px 14px' }}>
          <div className="markdown-content" style={{ whiteSpace: 'pre-wrap' }}>
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={markdownComponents}>
              {sanitizeMarkdownViz(toMdString(displayedAnswer))}
            </ReactMarkdown>
          </div>

          {leftOutNote && (
            <div style={{ ...NOTE_STYLE, marginTop: 12 }}>{leftOutNote}</div>
          )}

          {/* View-switcher buttons below the response text */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            {showViewSwitcher ? (
              <>
                <button onClick={() => setActiveAnswerView((prev) => ({ ...prev, [entry.index]: 'preliminary' }))} data-label="Concise" onTouchStart={handleBtnTouch} style={{ ...BTN_BASE, border: '2px solid var(--primary-color, #007bff)', background: activeView === 'preliminary' ? 'var(--primary-color, #007bff)' : 'var(--background-color, #ffffff)', color: activeView === 'preliminary' ? 'var(--background-color, #ffffff)' : 'var(--primary-color, #007bff)', fontWeight: activeView === 'preliminary' ? '600' : '500', cursor: 'pointer', boxShadow: activeView === 'preliminary' ? '0 2px 4px rgba(0,123,255,0.2)' : 'none' }} className="response-btn"><RiZoomOutLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Concise</span></button>
                <button onClick={() => setActiveAnswerView((prev) => ({ ...prev, [entry.index]: 'full' }))} data-label="Expansive" onTouchStart={handleBtnTouch} title={showExpansiveSpinner ? 'Generating… (click to watch)' : 'View Expansive'} style={{ ...BTN_BASE, border: '2px solid var(--primary-color, #007bff)', background: activeView === 'full' ? 'var(--primary-color, #007bff)' : 'var(--background-color, #ffffff)', color: activeView === 'full' ? 'var(--background-color, #ffffff)' : 'var(--primary-color, #007bff)', fontWeight: activeView === 'full' ? '600' : '500', cursor: 'pointer', boxShadow: activeView === 'full' ? '0 2px 4px rgba(0,123,255,0.2)' : 'none' }} className={`response-btn${showExpansiveSpinner ? ' response-btn-generating' : ''}`}><RiZoomInLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">{showExpansiveSpinner ? 'Generating…' : 'Expansive'}</span>{showExpansiveSpinner && (<div className="loading-spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: 'var(--primary-color, #007bff)' }}></div>)}</button>
                <button disabled={isEvaluationButtonDisabled} onClick={() => { if (canOpenEvaluationView) { setActiveAnswerView((prev) => ({ ...prev, [entry.index]: 'evaluation' })); } else if (!isRunning) { handleEvalClick(entry.index); } }} style={{ ...BTN_BASE, border: activeView === 'evaluation' ? '2px solid var(--primary-color, #007bff)' : entry.evaluation_report ? '2px solid var(--primary-color, #007bff)' : '2px solid #28a745', background: activeView === 'evaluation' ? 'var(--primary-color, #007bff)' : entry.evaluation_report ? 'var(--background-color, #ffffff)' : '#28a745', color: activeView === 'evaluation' ? 'var(--background-color, #ffffff)' : entry.evaluation_report ? 'var(--primary-color, #007bff)' : '#ffffff', fontWeight: activeView === 'evaluation' ? '600' : '500', cursor: isEvaluationButtonDisabled ? 'not-allowed' : 'pointer', opacity: showEvaluationSpinner ? 1 : isEvaluationButtonDisabled ? 0.6 : 1, boxShadow: activeView === 'evaluation' ? '0 2px 4px rgba(0,123,255,0.2)' : 'none' }} title={evaluationTooltip} data-label="Evaluation" onTouchStart={handleBtnTouch} className={`response-btn${showEvaluationSpinner ? ' response-btn-generating' : ''}`}><RiListCheck3 size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">{showEvaluationSpinner ? 'Generating…' : 'Evaluation'}</span>{showEvaluationSpinner && (<div className="loading-spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }}></div>)}</button>
              </>
            ) : (
              <>
                {showStaleDataNudge && (
                  <div style={NOTE_STYLE}>
                    This answer is based on training data and may not reflect current information. Use the{' '}
                    <strong style={{ color: '#28a745' }}>Expansive</strong> button below to run a web-researched, up-to-date response.
                  </div>
                )}
                <button style={{ ...BTN_BASE, border: '2px solid var(--primary-color, #007bff)', background: 'var(--primary-color, #007bff)', color: 'var(--background-color, #ffffff)', fontWeight: '600', cursor: 'default', boxShadow: '0 2px 4px rgba(0,123,255,0.2)' }} data-label="Concise" onTouchStart={handleBtnTouch} className="response-btn"><RiZoomOutLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Concise</span></button>
                <button disabled={isRunning} onClick={() => { if (!isRunning) { handleExpansiveClick(entry.index); } }} style={{ ...BTN_BASE, border: '2px solid #28a745', background: '#28a745', color: '#ffffff', fontWeight: '600', cursor: isRunning ? 'not-allowed' : 'pointer', opacity: showExpansiveSpinner ? 1 : isRunning ? 0.6 : 1, boxShadow: isRunning ? 'none' : '0 2px 4px rgba(40,167,69,0.2)' }} title={expansiveGenerateTooltip} data-label="Expansive" onTouchStart={handleBtnTouch} className={`response-btn${showExpansiveSpinner ? ' response-btn-generating' : ''}`}><RiZoomInLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">{showExpansiveSpinner ? 'Generating…' : 'Expansive'}</span>{showExpansiveSpinner && (<div className="loading-spinner" style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }}></div>)}</button>
                <button disabled={true} style={{ ...BTN_BASE, border: '2px solid var(--border-color, #ddd)', background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-secondary, #999)', fontWeight: '500', cursor: 'not-allowed', opacity: 0.6 }} title="Evaluation (Requires Expansive Answer)" data-label="Evaluation" onTouchStart={handleBtnTouch} className="response-btn"><RiListCheck3 size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Evaluation</span></button>
              </>
            )}
          </div>

          {error && <RunError message={error} markdownComponents={markdownComponents} />}
        </div>
      </div>

    </div>
  );
});

const Response = ({
  response,
  loading,
  isActiveConvServerRunning = false,
  currentResponseRef,
  streamingActiveView,
  setStreamingActiveView,
  streamingPhase,
  pendingQuestion,
  pendingFiles,
  pendingFileObjectUrls,
  chatHistory,
  activeAnswerView,
  setActiveAnswerView,
  generatingExpansiveIndex,
  generatingEvaluationIndex,
  conversationId,
  API_URL,
  markdownComponents,
  handleExpansiveClick,
  handleEvalClick,
  progressSteps = [],
}) => {
  // Build a single synthetic progress step for cross-session display.
  // The message is derived from the latest chat entry's state so users
  // see a meaningful hint without needing a live SSE stream.
  // A stable 'id' prevents the InlineProgressCard from re-mounting on
  // every render while the 3-second poll fires.
  const crossSessionProgressSteps = (() => {
    if (!isActiveConvServerRunning || loading) return [];
    const sorted = chatHistory ? [...chatHistory].sort((a, b) => {
      const ai = typeof a.index === 'number' ? a.index : 0;
      const bi = typeof b.index === 'number' ? b.index : 0;
      return ai - bi;
    }) : [];
    const latest = sorted[sorted.length - 1];
    const hasExpansive = Boolean(
      latest?.preliminary_answer &&
      latest.preliminary_answer !== latest?.assistant
    );
    const message = !latest
      ? 'Processing your request in the background...'
      : hasExpansive
        ? 'Agents working in the background...'
        : 'Running agent research in the background...';
    return [{ status: 'cross_session_running', message, timestamp: 0, id: 'cross-session' }];
  })();

  // True once the EXPANSIVE phase begins — matches its earliest status
  // ("Starting in-depth analysis...") through agent dispatch, all of which
  // precede result_generating/streamingPhase === 'full'. The
  // "in-depth analysis" marker keeps the green button from sitting idle
  // during the auth/agent-discovery window.
  // Do NOT match "available agent" here: the concise/setup phase also
  // emits "Found N available agent(s)..." (ask.py) BEFORE the concise
  // answer streams, which would light the Expansive spinner while the
  // concise answer is still streaming. The expansive-only markers below
  // never fire during the concise phase.
  const isExpansiveInProgress = loading && progressSteps.some(
    (s) => /calling agent|in-depth analysis|generating expansive/i.test(s.message),
  );
  const [btnTooltip, setBtnTooltip] = useState(null);
  const btnTooltipTimer = useRef(null);
  useEffect(() => () => clearTimeout(btnTooltipTimer.current), []);

  const handleBtnTouch = useCallback((e) => {
    const label = e.currentTarget.getAttribute('data-label');
    if (!label) return;
    clearTimeout(btnTooltipTimer.current);
    setBtnTooltip(label);
    btnTooltipTimer.current = setTimeout(() => setBtnTooltip(null), 2000);
  }, []);

  // An error from a run asked for on an answer goes under that answer, by the
  // button that started it.  Any other goes where the run's answer would have.
  const errorEntryIndex =
    response?.error && chatHistory?.some((e) => e.index === response.entryIndex)
      ? response.entryIndex
      : null;

  return (
    <>
      {/* Chat history — oldest first */}
      {chatHistory && chatHistory.length > 0 && (() => {
        const sortedHistory = [...chatHistory].sort((a, b) => {
          const ai = typeof a.index === 'number' ? a.index : 0;
          const bi = typeof b.index === 'number' ? b.index : 0;
          return ai - bi;
        });
        const latestEntry = sortedHistory.length > 0 ? sortedHistory[sortedHistory.length - 1] : null;

        return (
          <div style={{ paddingTop: '1rem' }}>
            {sortedHistory.map((entry, index) => {
              const entryKey = entry.index ?? index;
              return (
                <HistoryEntry
                  key={entryKey}
                  entryKey={entryKey}
                  entry={entry}
                  error={errorEntryIndex === entry.index ? response.error : null}
                  activeView={activeAnswerView[entry.index] || 'preliminary'}
                  isLatestEntry={latestEntry !== null && entry.index === latestEntry.index}
                  loading={loading}
                  isActiveConvServerRunning={isActiveConvServerRunning}
                  streamingPhase={streamingPhase}
                  generatingExpansiveIndex={generatingExpansiveIndex}
                  generatingEvaluationIndex={generatingEvaluationIndex}
                  isExpansiveInProgress={isExpansiveInProgress}
                  conversationId={conversationId}
                  API_URL={API_URL}
                  markdownComponents={markdownComponents}
                  setActiveAnswerView={setActiveAnswerView}
                  handleExpansiveClick={handleExpansiveClick}
                  handleEvalClick={handleEvalClick}
                  handleBtnTouch={handleBtnTouch}
                />
              );
            })}
          </div>
        );
      })()}

      {/* Pending question — right-aligned blue bubble */}
      {pendingQuestion && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
            <div style={{ maxWidth: '80%' }}>
              <FileAttachments
                files={pendingFiles}
                fileObjectUrls={pendingFileObjectUrls}
              />
              <div style={BUBBLE_STYLE}>{pendingQuestion}</div>
            </div>
          </div>
        </div>
      )}

      {response && response.error && errorEntryIndex === null && (
        <RunError message={response.error} markdownComponents={markdownComponents} />
      )}

      {/* Live streaming response — left-aligned dark card */}
      {response && !response.error && (response.summary || response.preliminaryAnswer) && (
        <div ref={currentResponseRef} style={{ marginTop: '0.5rem', marginBottom: 8 }}>
          <div style={CARD_STYLE}>
            <div style={CARD_HEADER_STYLE}>
              <RiStackLine size={15} />
              <span style={CARD_LABEL_STYLE}>Stack</span>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <div className="markdown-content" style={{ whiteSpace: 'pre-wrap' }}>
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={markdownComponents}>
                  {sanitizeMarkdownViz(toMdString(streamingActiveView === 'preliminary' && response.preliminaryAnswer ? response.preliminaryAnswer : response.summary))}
                </ReactMarkdown>
              </div>

              {loading && response && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => setStreamingActiveView('preliminary')} disabled={!response.preliminaryAnswer} style={{ ...BTN_BASE, border: '2px solid var(--primary-color, #007bff)', background: streamingActiveView === 'preliminary' && response.preliminaryAnswer ? 'var(--primary-color, #007bff)' : response.preliminaryAnswer ? 'var(--background-color, #ffffff)' : 'var(--bg-secondary, #f5f5f5)', color: streamingActiveView === 'preliminary' && response.preliminaryAnswer ? 'var(--background-color, #ffffff)' : response.preliminaryAnswer ? 'var(--primary-color, #007bff)' : 'var(--text-secondary, #999)', fontWeight: streamingActiveView === 'preliminary' && response.preliminaryAnswer ? '600' : '500', cursor: response.preliminaryAnswer ? 'pointer' : 'not-allowed', boxShadow: streamingActiveView === 'preliminary' && response.preliminaryAnswer ? '0 2px 4px rgba(0,123,255,0.2)' : 'none', opacity: response.preliminaryAnswer ? 1 : 0.6 }} onMouseEnter={(e) => { if (streamingActiveView !== 'preliminary' && response?.preliminaryAnswer) { e.currentTarget.style.backgroundColor = 'var(--primary-color, #007bff)'; e.currentTarget.style.color = 'var(--background-color, #ffffff)'; } }} onMouseLeave={(e) => { if (streamingActiveView !== 'preliminary' && response?.preliminaryAnswer) { e.currentTarget.style.backgroundColor = 'var(--background-color, #ffffff)'; e.currentTarget.style.color = 'var(--primary-color, #007bff)'; } }} title={!response.preliminaryAnswer ? 'Concise answer not yet available' : ''} data-label="Concise" onTouchStart={handleBtnTouch} className="response-btn"><RiZoomOutLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Concise</span></button>
                  {/* Streaming Expansive toggle. Appearance is a pure function
                      of the stable streamingPhase (single source of truth for
                      the active phase), NOT progress-step text — so during the
                      concise/preliminary phase it stays a plain, disabled
                      button with no spinner and no flicker, and during the
                      expansive phase it is a green, clickable "view it as it
                      streams" affordance. The page-level loader and Agentic
                      Flow tracker convey that generation is still in progress. */}
                  <button onClick={() => setStreamingActiveView('full')} disabled={streamingPhase !== 'full'} style={{ ...BTN_BASE, background: streamingActiveView === 'full' && streamingPhase === 'full' ? 'var(--primary-color, #007bff)' : streamingPhase === 'full' ? '#28a745' : 'var(--bg-secondary, #f5f5f5)', color: streamingPhase === 'full' ? 'var(--background-color, #ffffff)' : 'var(--text-secondary, #999)', fontWeight: streamingPhase === 'full' ? '600' : '500', cursor: streamingPhase === 'full' ? 'pointer' : 'not-allowed', border: streamingActiveView === 'full' && streamingPhase === 'full' ? '2px solid var(--primary-color, #007bff)' : streamingPhase === 'full' ? '2px solid #28a745' : '2px solid var(--primary-color, #007bff)', boxShadow: streamingActiveView === 'full' && streamingPhase === 'full' ? '0 2px 4px rgba(0,123,255,0.2)' : streamingPhase === 'full' ? '0 2px 4px rgba(40,167,69,0.2)' : 'none', opacity: streamingPhase === 'full' ? 1 : 0.6 }} onMouseEnter={(e) => { if (streamingActiveView !== 'full' && streamingPhase === 'full') { e.currentTarget.style.backgroundColor = 'var(--primary-color, #007bff)'; e.currentTarget.style.color = 'var(--background-color, #ffffff)'; } }} onMouseLeave={(e) => { if (streamingActiveView !== 'full' && streamingPhase === 'full') { e.currentTarget.style.backgroundColor = '#28a745'; e.currentTarget.style.color = 'var(--background-color, #ffffff)'; } }} title={streamingPhase === 'full' ? 'View Expansive' : 'Preparing expansive answer…'} data-label="Expansive" onTouchStart={handleBtnTouch} className="response-btn"><RiZoomInLine size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Expansive</span></button>
                  <button disabled={true} style={{ ...BTN_BASE, border: '2px solid var(--border-color, #ddd)', background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-secondary, #999)', fontWeight: '500', cursor: 'not-allowed', opacity: 0.6 }} title="Available after initial response completes" data-label="Evaluation" onTouchStart={handleBtnTouch} className="response-btn"><RiListCheck3 size={14} style={{ flexShrink: 0 }} /><span className="response-btn-label">Evaluation</span></button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agentic workflow progress — below the streaming response while
          processing.  Also shown cross-session (reconnected browser) so
          users can see that work is still running in the background. */}
      {loading && progressSteps.length > 0 && (
        <InlineProgressCard steps={progressSteps} title="Agentic Flow" />
      )}
      {!loading && crossSessionProgressSteps.length > 0 && (
        <InlineProgressCard steps={crossSessionProgressSteps} title="Agentic Flow" />
      )}

      {btnTooltip && (
        <div className="stack-tooltip" style={{ position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
          {btnTooltip}
        </div>
      )}
    </>
  );
};

export default Response;
